/**
 * 项目状态管理（纯内存）。
 *
 * 重构说明：
 * 1. 历史上 projectStore 同时承担"内存列表"和"Dexie 持久化入口"双重角色；
 * 2. 本重构后，持久化全部落到 editorPreferencesRepo，store 只保留内存 context；
 * 3. 因此这里不再 import projectRepo，也不再有 watcher / updateProject 等异步方法。
 */

import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Project } from '@puppycad/cad-core';

export const useProjectStore = defineStore('project', () => {
  const currentProject = ref<Project | null>(null);
  const projects = ref<Project[]>([]);

  const hasCurrentProject = computed(() => currentProject.value !== null);
  const projectCount = computed(() => projects.value.length);

  function setCurrentProject(project: Project | null): void {
    currentProject.value = project;
  }

  function setProjects(projectList: Project[]): void {
    projects.value = projectList;
  }

  function addProject(project: Project): void {
    projects.value.push(project);
  }

  function removeProject(id: string): void {
    projects.value = projects.value.filter((project) => project.id !== id);
    if (currentProject.value?.id === id) {
      currentProject.value = null;
    }
  }

  function getProjectById(id: string): Project | undefined {
    return projects.value.find((project) => project.id === id);
  }

  function clear(): void {
    currentProject.value = null;
    projects.value = [];
  }

  return {
    currentProject,
    projects,
    hasCurrentProject,
    projectCount,
    setCurrentProject,
    setProjects,
    addProject,
    removeProject,
    getProjectById,
    clear,
  };
});
